import {
  Box,
  Card,
  CardContent,
  Grid,
  Paper,
  Typography,
  createStyles,
  makeStyles,
} from '@material-ui/core';
import AccessTimeIcon from '@material-ui/icons/AccessTime';
import { format } from 'date-fns';
import React, { useContext, useEffect, useState } from 'react';
import {
  AccountDetail,
  AccountDetailParams,
  AccountParams,
  CommPreferencesParams,
  DeliveryType,
  Maybe,
  NotificationFor,
  PersonContact,
  ServicePointDetail,
} from '../../../__generated__/pge-types';
import { useTranslation } from '../../../hooks/useTranslation';
import mainColors from '../../../themes/main-colors';
import theme from '../../../themes/theme';
import Buttons from '../../buttons';
import RestorationStatus, {
  ACTIVE_RESTORATION_STEP,
} from '../../outage/outage-center/outage-map-panel/restoration-status/RestorationStatus';
import { formatEstimatedTimeOn } from '../../outage/outageUtils';
import { Model } from '../../outage/report-outage/serviceLocation.rules';
import UpdateOutageAlerts from '../../outage/outage-center/outage-notifications-popup/UpdateOutageAlerts';
import { getAccountNotificationsDetails } from '../../../queries/outage.query';
import { useApolloClient } from '@apollo/react-hooks';
import useUtils from '../../outage/outage-center/utils';
import { OutageStatusGraphic } from '../../outage_v2/components/outage-status-graphic';
import MapSearchIcon from '../../outage_v2/components/icons/MapSearchIcon';
import PowerIcon from '../../outage_v2/components/icons/PowerIcon';
import ReportOutageIcon from '../../outage_v2/components/icons/ReportOutageIcon';
import AlertIcon from '../../outage_v2/components/icons/AlertIcon';
import HelpIcon2 from '../../outage_v2/components/icons/HelpIcon2';
import { navigate } from '@reach/router';
import routes from '../../../routes';
import { OutageStatusFAQ } from '../../outage_v2/components/outage-status-faq';
import { getRestorationStatus } from '../../outage_v2/utils/utils';
import { RestorationStatusFAQ } from '../../outage_v2/components/restoration-status-faq';
import dayjs from 'dayjs';
import { DefaultViewParamState } from '../../outage_v2/types';
import { useIsMobile } from '../../../util/style-utils';
import { featureFlagsContext } from '../../../providers/FeatureFlagsProvider';

const useStyles = (isMobile?: boolean) =>
  makeStyles(() =>
    createStyles({
      container: {
        boxShadow: 'inset 0px 2px 0px #F06D04, 0px 3px 6px #00000029',
        backgroundColor: mainColors.lightBlue1,
      },
      title: {
        fontWeight: 500,
        fontSize: '20px',
        fontFamily: 'Forma-DJR-Display',
        margin: 0,
      },
      restorationStatus: {
        cursor: 'pointer',
        textDecoration: 'underline',
        color: mainColors.sparkBlue,
      },
      linkItem: {
        display: 'flex',
        justifyContent: 'space-between',
        padding: '20px 14px',
        cursor: 'pointer',
        marginBottom: '14px',
        '& .MuiTypography-root': {
          fontWeight: 600,
        },
      },
      outageAction: {
        display: isMobile ? 'grid' : 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        justifyItems: isMobile ? 'center' : 'initial',
        padding: '24px 0',
        gap: '8px',
        '& .MuiTypography-root': {
          fontWeight: 600,
        },
      },
    }),
  );

type OutageCardProps = {
  outageDetails?: ServicePointDetail;
  accountParams: AccountParams | undefined;
  customerContact: Maybe<PersonContact>[] | undefined | null;
};
export const OutageCard = (props: OutageCardProps) => {
  const isMobile = useIsMobile();
  const classes = useStyles(isMobile)();
  const { t, inlineRichT } = useTranslation();
  const { outageDetails, accountParams, customerContact } = props;
  const [openOutageStatusFAQ, setOpenOutageStatusFAQ] = React.useState(false);
  const [
    openRestorationStatusFAQ,
    setOpenRestorationStatusFAQ,
  ] = React.useState(false);
  const {
    isNestedOutageEnabled: nestedOutagesFeatureFlag,
    loading: featureFlagLoading,
  } = useContext(featureFlagsContext);
  const isNestedOutageEnabled = !featureFlagLoading && nestedOutagesFeatureFlag;

  //Moved this functionalify for notification to outages page during outages v2 implementation
  /*
  const [activeStep, setActiveStep] = React.useState<ACTIVE_RESTORATION_STEP>(
    ACTIVE_RESTORATION_STEP.POWER_OUT,
  );
  const [notificationFormValues, setNotificationFormValues] = useState<Model>({
    receiveEmail: false,
    email: '',
    receiveText: false,
    textNumber: '',
    callbackPhoneNumber: '',
    receiveCallBack: false,
    encryptedEmailContactId: '',
    encryptedTextNumberContactId: '',
  });
  const [
    isChangeNotificationSettingsPopup,
    setIsChangeNotificationSettingsPopup,
  ] = useState<boolean>(false);

  const [activeStepDetails, setActiveStepDetails] = React.useState<{
    icon: React.ReactNode;
    title: string;
  }>({
    icon: <PowerOut />,
    title: t('POWER_OUT'),
  });
  useEffect(() => {
    if (activeStep === ACTIVE_RESTORATION_STEP.CREW_DISPATCHED) {
      setActiveStepDetails({
        icon: <CrewDispatched />,
        title: t('CREW_DISPATCHED'),
      });
    }
    if (activeStep === ACTIVE_RESTORATION_STEP.CAUSE_DETERMINED) {
      setActiveStepDetails({
        icon: <CauseDetermined />,
        title: t('CAUSE_DETERMINED'),
      });
    }
    if (activeStep === ACTIVE_RESTORATION_STEP.CREW_ONSITE) {
      setActiveStepDetails({
        icon: <CrewOnsite />,
        title: t('CREW_ONSITE'),
      });
    }
  }, [activeStep]);

  const { setErrorNotification } = useUtils();
  const apolloClient = useApolloClient();

  const defaultFormValues: Model = {
    receiveEmail: false,
    email: '',
    receiveText: false,
    textNumber: '',
    callbackPhoneNumber: '',
    receiveCallBack: false,
    encryptedEmailContactId: '',
    encryptedEmailPreferenceId: '',
    encryptedTextNumberContactId: '',
    encryptedTextNumberPreferenceId: '',
  };
  const setupForm = (accountDetails: AccountDetail | undefined) => {
    if (Boolean(accountDetails?.commPreferences)) {
      accountDetails?.commPreferences &&
        accountDetails?.commPreferences[0]?.deliveryTypeDetails?.map(
          contactItem => {
            if (contactItem?.contactDetails) {
              if (contactItem?.deliveryType === DeliveryType.Email) {
                defaultFormValues.receiveEmail = Boolean(
                  contactItem.contactDetails[0]?.isSelected,
                );
                defaultFormValues.email =
                  contactItem.contactDetails[0]?.value || '';
                defaultFormValues.encryptedEmailContactId =
                  contactItem.contactDetails[0]?.encryptedContactId || '';
                defaultFormValues.encryptedEmailPreferenceId =
                  contactItem.contactDetails[0]?.encryptedPreferenceId || '';
              } else if (contactItem?.deliveryType === DeliveryType.Sms) {
                defaultFormValues.receiveText = Boolean(
                  contactItem.contactDetails[0]?.isSelected,
                );
                defaultFormValues.textNumber =
                  contactItem.contactDetails[0]?.value?.replace(/\D/g, '') ||
                  '';
                defaultFormValues.encryptedTextNumberContactId =
                  contactItem.contactDetails[0]?.encryptedContactId || '';
                defaultFormValues.encryptedTextNumberPreferenceId =
                  contactItem.contactDetails[0]?.encryptedPreferenceId || '';
              }
            }
          },
        );
    }

    if (!Boolean(defaultFormValues.email)) {
      const emailContact = customerContact?.find(contact => {
        return (
          (contact?.contactType === 'EMAIL' ||
            contact?.contactType === 'BEMAIL') &&
          contact?.isPrimary === true
        );
      });
      defaultFormValues.email = emailContact?.contactValue || '';
      defaultFormValues.receiveEmail = false;
      defaultFormValues.encryptedEmailContactId =
        (!Boolean(defaultFormValues.encryptedEmailContactId) &&
          emailContact?.encryptedContactId) ||
        '';
      defaultFormValues.encryptedEmailPreferenceId = null;
    }
    if (!Boolean(defaultFormValues.textNumber)) {
      const textContact = customerContact?.find(contact => {
        return contact?.contactType === 'MOB';
      });
      defaultFormValues.textNumber =
        textContact?.contactValue?.replace(/\D/g, '') || '';
      defaultFormValues.receiveText = false;
      defaultFormValues.encryptedTextNumberContactId =
        (!Boolean(defaultFormValues.encryptedTextNumberContactId) &&
          textContact?.encryptedContactId) ||
        '';
      defaultFormValues.encryptedTextNumberPreferenceId = null;
    }

    if (!Boolean(defaultFormValues.callbackPhoneNumber)) {
      const pnpPhone = customerContact?.find(contact => {
        return contact?.contactType === 'PNP';
      });
      if (pnpPhone) {
        defaultFormValues.callbackPhoneNumber =
          pnpPhone?.contactValue?.replace(/\D/g, '') || '';
      } else {
        const mobPhone = customerContact?.find(contact => {
          return contact?.contactType === 'MOB';
        });
        defaultFormValues.callbackPhoneNumber =
          mobPhone?.contactValue?.replace(/\D/g, '') || '';
      }
    }

    setNotificationFormValues(defaultFormValues);
    if (
      defaultFormValues.receiveCallBack ||
      defaultFormValues.receiveEmail ||
      defaultFormValues.receiveText
    ) {
      //setShowNotifSuccessfullySubscribed(true);
    }
  };

  const getNotificationPreferences = async () => {
    try {
      const result = await apolloClient.query<
        { getAccountDetails: Array<AccountDetail> },
        {
          params: AccountDetailParams;
          commPreferencesParams: CommPreferencesParams;
        }
      >({
        query: getAccountNotificationsDetails,
        variables: {
          params: {
            accountNumberList: [accountParams!],
          },
          commPreferencesParams: {
            notificationFor: NotificationFor.Outage,
          },
        },
        fetchPolicy: 'network-only',
      });
      setupForm(result?.data?.getAccountDetails[0]);
    } catch {
      setErrorNotification(true);
    }
  };

  useEffect(() => {
    void getNotificationPreferences();
  }, []); */
  return (
    <>
      <Paper className={classes.container}>
        <Box padding={'16px 4px 4px'}>
          <Grid container>
            <Grid item>
              <Typography
                className={classes.title}
                data-testid="card-title"
                css={{ paddingLeft: theme.spacing(2) }}
              >
                {t('OUTAGE_REPORTED')}
              </Typography>
            </Grid>
          </Grid>

          <Grid container spacing={2}>
            {isNestedOutageEnabled && (
              <Grid item xs={12} sm={4}>
                <OutageStatusGraphic
                  step={0}
                  hasKnownOutage={outageDetails?.outageInfo?.hasKnownOutage}
                  height={{ md: 134, sm: 208 }}
                />
              </Grid>
            )}

            <Grid item xs={12} sm={isNestedOutageEnabled ? 4 : 6}>
              <Card elevation={0}>
                <Box p={2} paddingBottom={0}>
                  <Typography variant="subtitle1">
                    {t('ESTIMATED_RESTORATION_NEW')}:
                  </Typography>
                  <Typography variant="body2" css={{ fontSize: '16px' }}>
                    <strong>{`${
                      outageDetails?.outageInfo?.estimatedTimeOn
                        ? dayjs(
                            outageDetails?.outageInfo?.estimatedTimeOn,
                            'MMMM D, YYYY h:mm a',
                          )
                            .format('MMM D, h:mm a')
                            .replace('am', 'a.m.')
                            .replace('pm', 'p.m.')
                        : t('TO_BE_DETERMINED')
                    }`}</strong>
                  </Typography>
                </Box>
                <Box p={2} data-testid="estimated-restoration">
                  <Typography variant="subtitle1">
                    {t('RESTORATION_STATUS_NEW')}:
                  </Typography>
                  <Typography
                    variant="body2"
                    className={classes.restorationStatus}
                    css={{ fontSize: '16px' }}
                    onClick={() => setOpenRestorationStatusFAQ(true)}
                  >
                    <strong>
                      {
                        getRestorationStatus(
                          outageDetails?.outageInfo?.eventStatus || '',
                        ).title
                      }
                    </strong>
                  </Typography>
                </Box>
              </Card>
            </Grid>

            <Grid item xs={12} sm={isNestedOutageEnabled ? 4 : 6}>
              <Card
                elevation={0}
                className={classes.linkItem}
                onClick={() => setOpenOutageStatusFAQ(true)}
              >
                <Typography variant="subtitle1">{t('OUTAGE_FAQ')}</Typography>
                <HelpIcon2 width={24} height={24} />
              </Card>
              <Card
                elevation={0}
                className={classes.linkItem}
                onClick={() =>
                  navigate(
                    `${routes.OUTAGES}?account=${accountParams?.accountNumber}`,
                  )
                }
              >
                <Typography variant="subtitle1">
                  {t('VIEW_OUTAGE_MAP_BUTTON')}
                </Typography>
                <MapSearchIcon size={24} />
              </Card>
            </Grid>
          </Grid>

          <Box css={{ marginTop: theme.spacing(1) }}>
            <Grid container spacing={0}>
              <Grid
                item
                xs={4}
                sm={4}
                css={{ paddingBottom: 0, paddingRight: theme.spacing(0.25) }}
              >
                <Card
                  elevation={0}
                  className={classes.outageAction}
                  css={{ height: '100%', cursor: 'pointer' }}
                  onClick={() =>
                    navigate(
                      routes.OUTAGES +
                        `?account=${accountParams?.accountNumber}&view=${DefaultViewParamState.MY_POWER_IS_ON}`,
                      { state: { account: accountParams?.accountNumber } },
                    )
                  }
                >
                  <PowerIcon />
                  <Typography variant="subtitle1">
                    {t('MY_POWER_IS_ON')}
                  </Typography>
                </Card>
              </Grid>
              <Grid
                item
                xs={4}
                sm={4}
                css={{ paddingBottom: 0, paddingRight: theme.spacing(0.25) }}
              >
                <Card
                  elevation={0}
                  className={classes.outageAction}
                  css={{ cursor: 'pointer' }}
                  onClick={() =>
                    navigate(
                      routes.OUTAGES +
                        `?account=${accountParams?.accountNumber}&view=${DefaultViewParamState.REPORT_OUTAGE}`,
                      { state: { account: accountParams?.accountNumber } },
                    )
                  }
                >
                  <ReportOutageIcon />
                  <Typography variant="subtitle1">
                    {t('REPORT_OUTAGE')}
                  </Typography>
                </Card>
              </Grid>
              <Grid item xs={4} sm={4} css={{ paddingBottom: 0 }}>
                <Card
                  elevation={0}
                  className={classes.outageAction}
                  css={{ cursor: 'pointer' }}
                  onClick={() =>
                    navigate(
                      routes.OUTAGES +
                        `?account=${accountParams?.accountNumber}&view=${DefaultViewParamState.NOTIFICATION}`,
                      { state: { account: accountParams?.accountNumber } },
                    )
                  }
                >
                  <AlertIcon />
                  <Typography variant="subtitle1">
                    {t('NOTIFICATIONS')}
                  </Typography>
                </Card>
              </Grid>
            </Grid>
          </Box>

          <Box css={{ marginTop: theme.spacing(0.25) }}>
            <Card elevation={0}>
              <Grid
                container
                alignItems="center"
                justify={isMobile ? 'center' : 'space-between'}
                css={{ padding: theme.spacing(1, 3) }}
              >
                <Grid item>
                  <Typography variant="subtitle1">
                    {inlineRichT('OUTAGE_LAST_UPDATED_DASHBOARD_CARD', {
                      OUTAGE_UPDATED_DATE: dayjs(
                        Date.now(),
                        'MMMM D, YYYY h:mm a',
                      )
                        .format('MMM D, h:mm a')
                        .replace('May', 'May')
                        .replace('am', 'a.m.')
                        .replace('pm', 'p.m.'),
                    })}
                  </Typography>
                </Grid>
                <Grid item>
                  <Typography variant="subtitle1">
                    {inlineRichT(
                      'INFORMATION_REFRESH_EVERY_10_MINUTES_DASHBOARD_CARD',
                    )}
                  </Typography>
                </Grid>
              </Grid>
            </Card>
          </Box>
        </Box>
      </Paper>
      <OutageStatusFAQ
        open={openOutageStatusFAQ}
        setOpen={setOpenOutageStatusFAQ}
      />

      <RestorationStatusFAQ
        open={openRestorationStatusFAQ}
        setOpen={setOpenRestorationStatusFAQ}
      />
      {/* isChangeNotificationSettingsPopup && (
        <UpdateOutageAlerts
          setNotifFormSuccessfullySubmitted={
            setIsChangeNotificationSettingsPopup
          }
          setNotificationFormValues={setNotificationFormValues}
          open={isChangeNotificationSettingsPopup}
          setIsChangeNotificationSettingsPopup={
            setIsChangeNotificationSettingsPopup
          }
          loggedInUserNotificationRequestAccountParams={{
            accountNumber: accountParams?.accountNumber!,
            encryptedPersonId: accountParams?.encryptedPersonId!,
          }}
          notificationFormValues={notificationFormValues}
        />
      ) */}
    </>
  );
};
